<template>
  <div id="loan-page">
    <div class="page-name">
      <div class="page-title">Loan</div>
    </div>
    <div class="page-container">
      <b-card class="w-100">
        <div class="flex-row align-center justify-between">
          <div class="flex-col" style="flex: 1">
            <h5 class="page-title">Loan Content</h5>
          </div>
        </div>
        <hr />

        <b-form-group style="font-weight: 600" label="Updated as of (Date)">
          <div style="position: relative; display: inline-block; width: 100%;">
            <b-form-input
              type="date"
              v-model="form.content_date"
              placeholder="Select date"
            ></b-form-input>
            <b-spinner
              v-if="isSavingContentDate"
              small
              style="position: absolute; right: 10px; top: 50%; transform: translateY(-50%);"
              variant="primary"
            ></b-spinner>
          </div>
        </b-form-group>

        <hr />

        <loan-bank-table
          :data.sync="form.banks"
          :onAddBank="onAddBank"
          :onUpdateBank="onUpdateBank"
          :onFetchBankList="fetchLoan"
          :onDeleteBank="onDeleteBank"
          :onSorting="onSorting"
        />
      </b-card>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import LoanBankTable from '@/components/tables/LoanBankTable'

export default {
  components: {
    LoanBankTable
  },
  data: () => ({
    form: {
      content_date: '',
      banks: []
    },
    isInitialLoad: true,
    contentDateSaveTimeout: null,
    isSavingContentDate: false
  }),
  watch: {
    'form.content_date'(newValue, oldValue) {
      // Skip auto-save during initial load
      if (this.isInitialLoad) {
        return
      }

      // Clear any existing timeout
      if (this.contentDateSaveTimeout) {
        clearTimeout(this.contentDateSaveTimeout)
      }

      // Set up debounced save with 500ms delay
      this.contentDateSaveTimeout = setTimeout(() => {
        this.autoSaveContentDate()
      }, 500)
    }
  },
  methods: {
    ...mapActions('loan', ['getLoan', 'updateLoan', 'createBank', 'updateBank']),
    async autoSaveContentDate() {
      if (!this.form.content_date) {
        return
      }

      this.isSavingContentDate = true

      try {
        await this.updateLoan({
          content_date: this.form.content_date,
          banks: this.form.banks
        })

        this.$bvToast.toast('Content date saved', {
          title: 'Success',
          autoHideDelay: 2000,
          appendToast: true,
          variant: 'success',
          toaster: 'b-toaster-bottom-right',
          solid: true
        })
      } catch (error) {
        const errorMessage = (error.response && error.response.data && error.response.data.error) || error.message || 'Failed to save content date'
        this.$bvToast.toast(errorMessage, {
          title: 'Error',
          autoHideDelay: 2000,
          appendToast: true,
          variant: 'danger',
          toaster: 'b-toaster-bottom-right',
          solid: true
        })
      } finally {
        this.isSavingContentDate = false
      }
    },
    onAddBank(bankData) {
      return this.createBank(bankData)
        .then((bank) => {
          // Refresh the form data from server to get the updated state
          return this.fetchLoan().then(() => ({
            success: true,
            message: 'Bank added successfully'
          }))
        })
        .catch((error) => {
          const errorMessage = (error.response && error.response.data && error.response.data.error) || error.message || 'Failed to add bank'
          this.$bvToast.toast(
            errorMessage,
            {
              title: 'Error',
              autoHideDelay: 2000,
              appendToast: true,
              variant: 'danger',
              toaster: 'b-toaster-bottom-right',
              solid: true
            }
          )
          return { success: false, message: error.message || 'Failed to add bank' }
        })
    },
    onUpdateBank(id, bankData) {
      return this.updateBank({ id, bankData })
        .then((bank) => {
          // Refresh the form data from server to get the updated state (including reordered banks)
          return this.fetchLoan().then(() => ({
            success: true,
            message: 'Bank updated successfully'
          }))
        })
        .catch((error) => {
          const errorMessage = (error.response && error.response.data && error.response.data.error) || error.message || 'Failed to update bank'
          this.$bvToast.toast(
            errorMessage,
            {
              title: 'Error',
              autoHideDelay: 2000,
              appendToast: true,
              variant: 'danger',
              toaster: 'b-toaster-bottom-right',
              solid: true
            }
          )
          return { success: false, message: error.message || 'Failed to update bank' }
        })
    },
    onDeleteBank(id, index) {
      return new Promise(resolve => {
        // Remove bank from array
        this.form.banks.splice(index, 1)
        // Update ordering for remaining banks
        this.form.banks.forEach((bank, idx) => {
          bank.ordering = idx
        })
        resolve({ success: true, message: 'Bank deleted successfully' })
      })
    },
    onSorting({ oldIndex, newIndex }) {
      return new Promise((resolve) => {
        // oldIndex and newIndex are already 0-based array indices
        if (
          oldIndex === newIndex ||
          oldIndex < 0 ||
          newIndex < 0 ||
          oldIndex >= this.form.banks.length ||
          newIndex >= this.form.banks.length
        ) {
          resolve({ success: true })
          return
        }

        // Get the bank being moved
        const bank = this.form.banks[oldIndex]
        if (!bank || !bank.id) {
          resolve({ success: true })
          return
        }

        // Calculate the new ordering value (0-based)
        const newOrdering = newIndex

        // Update the bank's ordering via API (this will trigger reordering on the server)
        // Only send the fields that need to be updated (preserve existing values)
        this.updateBank({
          id: bank.id,
          bankData: {
            translations: bank.translations || [],
            interest_rate: bank.interest_rate,
            interest_rate_mrta: bank.interest_rate_mrta,
            loan_limit: bank.loan_limit,
            loan_period: bank.loan_period,
            ordering: newOrdering,
            image_url: bank.image_url
          }
        })
          .then(() => {
            this.$bvToast.toast('Bank reordered successfully', {
              title: 'Complete',
              autoHideDelay: 2000,
              appendToast: true,
              variant: 'success',
              toaster: 'b-toaster-bottom-right',
              solid: true
            })
            // Refresh the form data from server to get the updated state
            return this.fetchLoan().then(() => {
              resolve({ success: true })
            })
          })
          .catch((error) => {
            const errorMessage = (error.response && error.response.data && error.response.data.error) || error.message || 'Failed to reorder banks'
            this.$bvToast.toast(
              errorMessage,
              {
                title: 'Error',
                autoHideDelay: 2000,
                appendToast: true,
                variant: 'danger',
                toaster: 'b-toaster-bottom-right',
                solid: true
              }
            )
            resolve({ success: false, message: error.message || 'Failed to reorder banks' })
          })
      })
    },
    fetchLoan() {
      return this.getLoan()
        .then(data => {
          if (data) {
            this.form = {
              content_date: data.content_date
                ? new Date(data.content_date).toISOString().split('T')[0]
                : new Date().toISOString().split('T')[0],
              banks: (data.banks || []).map(bank => ({
                id: bank.id,
                translations: bank.translations || [
                  { lang: 'th', name: '' },
                  { lang: 'en', name: '' }
                ],
                interest_rate: bank.interest_rate || 0,
                interest_rate_mrta: bank.interest_rate_mrta || 0,
                loan_limit: bank.loan_limit || 0,
                loan_period: bank.loan_period || 0,
                ordering: bank.ordering || 0,
                image_url: bank.image_url || ''
              }))
            }
          }
          // Set flag to false after initial load completes
          this.isInitialLoad = false
          return data
        })
        .catch(error => {
          this.$bvToast.toast(error.message || 'Failed to fetch loan content', {
            title: 'Error',
            autoHideDelay: 2000,
            appendToast: true,
            variant: 'danger',
            toaster: 'b-toaster-bottom-right',
            solid: true
          })
          // Set flag to false even on error
          this.isInitialLoad = false
          throw error
        })
    }
  },
  created() {
    this.fetchLoan()
  },
  beforeDestroy() {
    // Clean up timeout on component destroy
    if (this.contentDateSaveTimeout) {
      clearTimeout(this.contentDateSaveTimeout)
    }
  }
}
</script>

<style scoped>
.bank-item {
  background-color: #f9f9f9;
}
label {
  font-weight: bold;
}
</style>
